// QR scanner overlay. Resolves with the decoded text on the first
// successful read, `null` on user dismissal, or rejects when the
// device refuses camera access.
//
// Decode strategy (fastest → slowest, falls through on the fly):
//   1. `BarcodeDetector` native API (Chromium 88+, Android Chrome /
//      Edge, Safari 17+). Hardware-accelerated, low-latency, no
//      JS-decoder GC pauses. Polled at 30 fps via requestAnimationFrame.
//   2. `html5-qrcode` JS fallback for Firefox / older Safari /
//      anything else missing the native API. Same UX, just slower
//      decode.
//
// Visual contract — matches the rest of the auth surfaces:
//   - Full-viewport dim overlay with a centred "viewfinder" cutout.
//   - Solid corner markers (Lucide-style brackets) around the box.
//   - Animated scan line bouncing inside the box.
//   - Top bar: title + close button (X).
//   - Bottom bar: torch toggle (when supported) + camera switcher
//     (when >1 camera exposed by the OS).
//   - Haptic + brief flash on successful decode.
//
// API stability: `scanQrCode(): Promise<string | null>` — same shape
// the calling code already expects (see LinkDevicePairingHost +
// SettingsModalHost). All UI is mounted into `document.body` and
// torn down on resolve/reject.

import type { Html5Qrcode } from 'html5-qrcode';

/* ------------------------------ Style sheet ------------------------------ */

// All overlay markup uses raw CSS via a single injected <style> tag.
// We deliberately avoid Tailwind here: the overlay is mounted into
// `document.body` outside the React tree, so utility-class hydration
// would not apply. Keeping the styles inline + scoped under a single
// root class keeps the surface self-contained and easy to debug.
const SCANNER_STYLE_ID = 'ping-qr-scanner-style';
const SCANNER_STYLES = `
.ping-qr-scanner {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.92);
  color: #F5F6F8;
  font-family: 'Lato', -apple-system, system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: env(safe-area-inset-top, 16px) 16px env(safe-area-inset-bottom, 16px);
  user-select: none;
  -webkit-user-select: none;
}
.ping-qr-scanner__top {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  z-index: 2;
}
.ping-qr-scanner__title {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
}
.ping-qr-scanner__close {
  width: 36px;
  height: 36px;
  border-radius: 18px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #F5F6F8;
  font-size: 18px;
  line-height: 36px;
  text-align: center;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
}
.ping-qr-scanner__close:hover,
.ping-qr-scanner__close:focus-visible {
  background: rgba(255, 255, 255, 0.16);
  outline: none;
}
.ping-qr-scanner__stage {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.ping-qr-scanner__viewport {
  position: relative;
  width: min(78vmin, 360px);
  aspect-ratio: 1;
  border-radius: 24px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
}
.ping-qr-scanner__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}
.ping-qr-scanner__frame {
  position: absolute;
  inset: 6%;
  pointer-events: none;
}
.ping-qr-scanner__corner {
  position: absolute;
  width: 32px;
  height: 32px;
  border-color: #FFFFFF;
  border-style: solid;
  border-width: 0;
}
.ping-qr-scanner__corner--tl { top: 0; left: 0; border-top-width: 3px; border-left-width: 3px; border-top-left-radius: 14px; }
.ping-qr-scanner__corner--tr { top: 0; right: 0; border-top-width: 3px; border-right-width: 3px; border-top-right-radius: 14px; }
.ping-qr-scanner__corner--bl { bottom: 0; left: 0; border-bottom-width: 3px; border-left-width: 3px; border-bottom-left-radius: 14px; }
.ping-qr-scanner__corner--br { bottom: 0; right: 0; border-bottom-width: 3px; border-right-width: 3px; border-bottom-right-radius: 14px; }
.ping-qr-scanner__scanline {
  position: absolute;
  left: 8%;
  right: 8%;
  height: 2px;
  background: linear-gradient(
    90deg,
    rgba(48, 144, 255, 0) 0%,
    rgba(48, 144, 255, 0.9) 50%,
    rgba(48, 144, 255, 0) 100%
  );
  box-shadow: 0 0 8px rgba(48, 144, 255, 0.7);
  animation: ping-qr-scan 2.4s ease-in-out infinite;
  pointer-events: none;
}
@keyframes ping-qr-scan {
  0%   { top: 12%;  opacity: 0;   }
  10%  {            opacity: 1;   }
  50%  { top: 88%;  opacity: 1;   }
  90%  {            opacity: 1;   }
  100% { top: 12%;  opacity: 0;   }
}
.ping-qr-scanner__hint {
  margin-top: 20px;
  font-size: 13px;
  color: rgba(245, 246, 248, 0.7);
  text-align: center;
  max-width: 280px;
  line-height: 1.4;
}
.ping-qr-scanner__bottom {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px;
  z-index: 2;
}
.ping-qr-scanner__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.10);
  color: #F5F6F8;
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 120ms ease, transform 80ms ease;
}
.ping-qr-scanner__pill:hover,
.ping-qr-scanner__pill:focus-visible {
  background: rgba(255, 255, 255, 0.18);
  outline: none;
}
.ping-qr-scanner__pill:active {
  transform: scale(0.97);
}
.ping-qr-scanner__pill--on {
  background: rgba(255, 209, 102, 0.18);
  border-color: rgba(255, 209, 102, 0.4);
  color: #FFD166;
}
.ping-qr-scanner__pill:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ping-qr-scanner__state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.4);
  text-align: center;
  padding: 24px;
}
.ping-qr-scanner__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #F5F6F8;
  border-radius: 50%;
  animation: ping-qr-spin 0.8s linear infinite;
}
@keyframes ping-qr-spin { to { transform: rotate(360deg); } }
.ping-qr-scanner__success {
  position: absolute;
  inset: 0;
  background: rgba(48, 207, 109, 0.18);
  animation: ping-qr-flash 320ms ease-out;
  pointer-events: none;
}
@keyframes ping-qr-flash {
  0%   { opacity: 0; }
  20%  { opacity: 1; }
  100% { opacity: 0; }
}
`;

function ensureStyles(): void {
  if (document.getElementById(SCANNER_STYLE_ID) !== null) return;
  const styleEl = document.createElement('style');
  styleEl.id = SCANNER_STYLE_ID;
  styleEl.textContent = SCANNER_STYLES;
  document.head.appendChild(styleEl);
}

/* ------------------------------ DOM scaffold ------------------------------ */

interface Scaffold {
  readonly root: HTMLElement;
  readonly video: HTMLVideoElement;
  readonly viewport: HTMLElement;
  readonly hint: HTMLElement;
  readonly close: HTMLButtonElement;
  readonly torchPill: HTMLButtonElement;
  readonly switchPill: HTMLButtonElement;
  readonly stateOverlay: HTMLElement;
}

function buildScaffold(): Scaffold {
  ensureStyles();
  const root = document.createElement('div');
  root.className = 'ping-qr-scanner';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Scan QR code');

  root.innerHTML = `
    <div class="ping-qr-scanner__top">
      <div class="ping-qr-scanner__title">Scan QR code</div>
      <button class="ping-qr-scanner__close" aria-label="Close scanner">✕</button>
    </div>
    <div class="ping-qr-scanner__stage">
      <div class="ping-qr-scanner__viewport">
        <video class="ping-qr-scanner__video" playsinline muted autoplay></video>
        <div class="ping-qr-scanner__frame">
          <div class="ping-qr-scanner__corner ping-qr-scanner__corner--tl"></div>
          <div class="ping-qr-scanner__corner ping-qr-scanner__corner--tr"></div>
          <div class="ping-qr-scanner__corner ping-qr-scanner__corner--bl"></div>
          <div class="ping-qr-scanner__corner ping-qr-scanner__corner--br"></div>
          <div class="ping-qr-scanner__scanline"></div>
        </div>
        <div class="ping-qr-scanner__state" data-state="loading">
          <div class="ping-qr-scanner__spinner"></div>
          <div>Starting camera…</div>
        </div>
      </div>
    </div>
    <div class="ping-qr-scanner__bottom">
      <button class="ping-qr-scanner__pill" data-role="torch" disabled hidden>
        <span>Flashlight</span>
      </button>
      <button class="ping-qr-scanner__pill" data-role="switch" disabled hidden>
        <span>Switch camera</span>
      </button>
    </div>
  `;

  const get = <T extends Element>(sel: string): T => {
    const el = root.querySelector<T>(sel);
    if (el === null) throw new Error(`scanQrCode: missing scaffold element ${sel}`);
    return el;
  };

  // Hint sits below the viewport when stage is tall enough. We append
  // it dynamically so the layout stays clean.
  const hint = document.createElement('div');
  hint.className = 'ping-qr-scanner__hint';
  hint.textContent = 'Point your camera at the QR code shown on the other device.';
  get<HTMLElement>('.ping-qr-scanner__stage').appendChild(hint);

  return {
    root,
    video: get<HTMLVideoElement>('video'),
    viewport: get<HTMLElement>('.ping-qr-scanner__viewport'),
    hint,
    close: get<HTMLButtonElement>('.ping-qr-scanner__close'),
    torchPill: get<HTMLButtonElement>('[data-role="torch"]'),
    switchPill: get<HTMLButtonElement>('[data-role="switch"]'),
    stateOverlay: get<HTMLElement>('.ping-qr-scanner__state'),
  };
}

function setState(scaffold: Scaffold, kind: 'loading' | 'error' | 'hidden', message?: string): void {
  const el = scaffold.stateOverlay;
  if (kind === 'hidden') {
    el.style.display = 'none';
    return;
  }
  el.style.display = 'flex';
  el.dataset.state = kind;
  if (kind === 'loading') {
    el.innerHTML = `
      <div class="ping-qr-scanner__spinner"></div>
      <div>${message ?? 'Starting camera…'}</div>
    `;
  } else {
    el.innerHTML = `
      <div style="font-size:32px;">⚠</div>
      <div style="max-width:240px;line-height:1.4;">${message ?? 'Camera unavailable.'}</div>
    `;
  }
}

/* ------------------------------ Camera control ------------------------------ */

type CameraSpec = { deviceId: string; label: string };

async function listCameras(): Promise<readonly CameraSpec[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Camera' }));
  } catch {
    return [];
  }
}

interface NativeBarcodeDetectorCtor {
  new (opts?: { formats?: string[] }): {
    detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
  };
  getSupportedFormats?: () => Promise<string[]>;
}

function getNativeDetectorCtor(): NativeBarcodeDetectorCtor | null {
  const w = window as unknown as { BarcodeDetector?: NativeBarcodeDetectorCtor };
  return typeof w.BarcodeDetector === 'function' ? w.BarcodeDetector : null;
}

async function nativeSupportsQR(): Promise<boolean> {
  const Ctor = getNativeDetectorCtor();
  if (Ctor === null) return false;
  try {
    const formats = (await Ctor.getSupportedFormats?.()) ?? [];
    return formats.includes('qr_code');
  } catch {
    // Some implementations expose the ctor but throw on the
    // static probe — fall through to a constructor-level probe.
    try {
      const _ = new Ctor({ formats: ['qr_code'] });
      void _;
      return true;
    } catch {
      return false;
    }
  }
}

/* ------------------------------ Main API ------------------------------ */

export async function scanQrCode(): Promise<string | null> {
  // Bail out early in non-browser environments (SSR, tests) so the
  // caller can fall back to the paste-only flow without a noisy
  // exception bubbling up.
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera not supported in this browser');
  }

  const scaffold = buildScaffold();
  document.body.appendChild(scaffold.root);

  let resolved = false;
  let activeStream: MediaStream | null = null;
  let cameras: readonly CameraSpec[] = [];
  let activeCameraIndex = -1;
  let nativeRafHandle: number | null = null;
  let fallbackScanner: Html5Qrcode | null = null;
  let onEsc: ((e: KeyboardEvent) => void) | null = null;

  // Recently-seen content-hash → epoch ms. Prevents accidental double-
  // fire of the same value when the camera holds steady on the same
  // QR for multiple frames before the resolve closure cleans up.
  const recentDecodes = new Map<string, number>();
  const DEDUPE_WINDOW_MS = 1500;

  return new Promise<string | null>((resolve, reject) => {
    const cleanup = async (value: string | null, err?: Error): Promise<void> => {
      if (resolved) return;
      resolved = true;

      if (nativeRafHandle !== null) cancelAnimationFrame(nativeRafHandle);
      if (fallbackScanner) {
        try {
          await fallbackScanner.stop();
        } catch {
          /* already stopped */
        }
        try {
          fallbackScanner.clear();
        } catch {
          /* ignore */
        }
      }
      if (activeStream) {
        for (const t of activeStream.getTracks()) {
          try {
            t.stop();
          } catch {
            /* ignore */
          }
        }
      }
      if (onEsc) document.removeEventListener('keydown', onEsc);
      if (scaffold.root.parentNode) scaffold.root.parentNode.removeChild(scaffold.root);

      if (err) reject(err);
      else resolve(value);
    };

    onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') void cleanup(null);
    };
    document.addEventListener('keydown', onEsc);
    scaffold.close.addEventListener('click', () => void cleanup(null));

    const onDecode = (text: string): void => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      const now = Date.now();
      const seenAt = recentDecodes.get(trimmed);
      if (seenAt !== undefined && now - seenAt < DEDUPE_WINDOW_MS) return;
      recentDecodes.set(trimmed, now);

      // Brief success flash + haptic so the user knows the read landed.
      const flash = document.createElement('div');
      flash.className = 'ping-qr-scanner__success';
      scaffold.viewport.appendChild(flash);
      try {
        navigator.vibrate?.(40);
      } catch {
        /* not available — silent */
      }
      // Let the flash render for one frame before tearing down so
      // the user sees the confirmation. 200 ms matches the flash's
      // visible peak.
      window.setTimeout(() => void cleanup(trimmed), 200);
    };

    void (async () => {
      // Pre-fetch the camera list so we can populate the switcher
      // pill (and know which camera to default to). Labels become
      // populated AFTER the first getUserMedia call (browser quirk),
      // so we re-fetch after stream start.
      cameras = await listCameras();

      const useNative = await nativeSupportsQR();

      const startWithSpec = async (spec: CameraSpec | null): Promise<void> => {
        // Tear down any active stream/scan loop from a previous camera
        // before flipping to a new one.
        if (nativeRafHandle !== null) {
          cancelAnimationFrame(nativeRafHandle);
          nativeRafHandle = null;
        }
        if (fallbackScanner) {
          try {
            await fallbackScanner.stop();
          } catch {
            /* ignore */
          }
        }
        if (activeStream) {
          for (const t of activeStream.getTracks()) {
            try {
              t.stop();
            } catch {
              /* ignore */
            }
          }
          activeStream = null;
        }

        setState(scaffold, 'loading', 'Starting camera…');

        // High-resolution capture + continuous autofocus is what makes a
        // QR shown on ANOTHER device's screen (glare, moiré, close focus)
        // resolve crisply: the decoder gets far more pixels per module and
        // the lens keeps the code sharp. All of these are "ideal"/advanced
        // hints — unsupported keys are ignored, never a hard failure.
        const baseVideo = spec
          ? { deviceId: { exact: spec.deviceId } }
          : { facingMode: { ideal: 'environment' } };
        const constraints: MediaStreamConstraints = {
          video: {
            ...baseVideo,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
            // `focusMode`/`exposureMode`/`whiteBalanceMode` aren't in the
            // standard lib.dom typings — cast the whole object.
            advanced: [
              { focusMode: 'continuous' },
              { exposureMode: 'continuous' },
              { whiteBalanceMode: 'continuous' },
            ],
          } as unknown as MediaTrackConstraints,
          audio: false,
        };

        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          const name = (err as Error).name ?? '';
          const message =
            name === 'NotAllowedError' || name === 'PermissionDeniedError'
              ? 'Camera access denied. Enable it in your browser settings to scan.'
              : name === 'NotFoundError'
              ? 'No camera detected on this device.'
              : 'Could not start the camera.';
          setState(scaffold, 'error', message);
          return;
        }
        activeStream = stream;

        // Re-assert continuous autofocus on the live track. Several
        // engines (notably Chrome on some Android stacks) ignore advanced
        // focus hints inside getUserMedia but honour them via
        // applyConstraints once the track is running — this is the single
        // biggest factor for reading a QR off a nearby glossy screen.
        try {
          const vTrack = stream.getVideoTracks()[0];
          const vCaps = (vTrack as unknown as {
            getCapabilities?: () => { focusMode?: string[] };
          }).getCapabilities?.();
          if (vTrack !== undefined && vCaps?.focusMode?.includes('continuous')) {
            await (vTrack as unknown as {
              applyConstraints: (c: unknown) => Promise<void>;
            }).applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
          }
        } catch {
          /* focus hint rejected — the fixed-focus stream still scans */
        }

        // Refresh the camera list once the OS grants labels.
        cameras = await listCameras();
        wireCameraPills();

        // Hook torch capability if the active video track exposes it.
        wireTorchPill(stream);

        scaffold.video.srcObject = stream;
        try {
          await scaffold.video.play();
        } catch {
          /* autoplay sometimes throws on Safari — ignore */
        }
        setState(scaffold, 'hidden');

        if (useNative) {
          startNativeLoop(stream);
        } else {
          await startFallbackScanner(spec?.deviceId);
        }
      };

      const wireCameraPills = (): void => {
        if (cameras.length < 2) {
          scaffold.switchPill.hidden = true;
          scaffold.switchPill.disabled = true;
          return;
        }
        scaffold.switchPill.hidden = false;
        scaffold.switchPill.disabled = false;
        scaffold.switchPill.onclick = (): void => {
          if (cameras.length === 0) return;
          activeCameraIndex = (activeCameraIndex + 1) % cameras.length;
          const next = cameras[activeCameraIndex];
          if (next !== undefined) void startWithSpec(next);
        };
      };

      const wireTorchPill = (stream: MediaStream): void => {
        const track = stream.getVideoTracks()[0];
        // `getCapabilities` is not in the lib.dom typings on older TS
        // libs; cast to a structural shape.
        const caps = (track as unknown as {
          getCapabilities?: () => { torch?: boolean };
        }).getCapabilities?.();
        if (!caps?.torch) {
          scaffold.torchPill.hidden = true;
          scaffold.torchPill.disabled = true;
          return;
        }
        let torchOn = false;
        scaffold.torchPill.hidden = false;
        scaffold.torchPill.disabled = false;
        scaffold.torchPill.onclick = async (): Promise<void> => {
          torchOn = !torchOn;
          scaffold.torchPill.classList.toggle('ping-qr-scanner__pill--on', torchOn);
          try {
            await (track as unknown as {
              applyConstraints: (c: { advanced: Array<{ torch: boolean }> }) => Promise<void>;
            }).applyConstraints({ advanced: [{ torch: torchOn }] });
          } catch {
            // Some Android stacks expose torch capability but reject
            // the constraint. Roll the UI back and disable the pill
            // so the user doesn't keep tapping a dead control.
            torchOn = false;
            scaffold.torchPill.classList.remove('ping-qr-scanner__pill--on');
            scaffold.torchPill.disabled = true;
          }
        };
      };

      const startNativeLoop = (_stream: MediaStream): void => {
        const Ctor = getNativeDetectorCtor();
        if (Ctor === null) return;
        const detector = new Ctor({ formats: ['qr_code'] });
        const tick = async (): Promise<void> => {
          if (resolved) return;
          // `readyState >= 2` (HAVE_CURRENT_DATA) → at least one
          // frame buffered. Detecting before that returns nothing
          // and just burns RAF slots.
          if (scaffold.video.readyState >= 2) {
            try {
              const results = await detector.detect(scaffold.video);
              if (results.length > 0) {
                const first = results[0];
                if (first?.rawValue) {
                  onDecode(first.rawValue);
                  return;
                }
              }
            } catch {
              // Native detector occasionally throws on partial frames.
              // Swallow and keep polling — the next frame usually
              // works.
            }
          }
          nativeRafHandle = requestAnimationFrame(() => void tick());
        };
        nativeRafHandle = requestAnimationFrame(() => void tick());
      };

      const startFallbackScanner = async (deviceId: string | undefined): Promise<void> => {
        const { Html5Qrcode: Cls } = await import('html5-qrcode');
        // We hand `html5-qrcode` ITS OWN target element so it can
        // mount its <canvas>/<video> without fighting ours. Hide
        // our pre-rendered video; html5-qrcode brings its own.
        scaffold.video.style.display = 'none';
        const fbHost = document.createElement('div');
        fbHost.style.cssText = 'position:absolute;inset:0;';
        fbHost.id = `ping-qr-fb-${Date.now()}`;
        scaffold.viewport.appendChild(fbHost);

        // Route html5-qrcode through the native BarcodeDetector when
        // present (constructor-level config) — much sharper + faster
        // than its JS zxing decoder.
        fallbackScanner = new Cls(fbHost.id, {
          verbose: false,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
        const sourceConstraint = deviceId !== undefined
          ? { deviceId: { exact: deviceId } }
          : { facingMode: 'environment' as const };
        try {
          await fallbackScanner.start(
            sourceConstraint,
            // 30 fps gives the JS decoder ~33 ms per frame to
            // converge — well under the 60 ms html5-qrcode needs on
            // a mid-range phone. Larger qrbox = larger detector ROI
            // so a slightly off-centre code still resolves.
            {
              fps: 30,
              qrbox: (vw: number, vh: number) => {
                const size = Math.floor(Math.min(vw, vh) * 0.72);
                return { width: size, height: size };
              },
              // Capture at HD so the JS decoder sees sharp modules even
              // when the source is another device's screen.
              videoConstraints: {
                ...sourceConstraint,
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                advanced: [{ focusMode: 'continuous' }],
              } as unknown as MediaTrackConstraints,
            },
            (decoded) => onDecode(decoded),
            () => {
              /* per-frame "no QR" — ignore */
            },
          );
        } catch (err) {
          setState(
            scaffold,
            'error',
            (err as Error).message || 'Could not start the camera.',
          );
        }
      };

      try {
        await startWithSpec(null);
      } catch (err) {
        await cleanup(null, err as Error);
      }
    })();
  });
}
